<template>
  <div class="upload-container">
    <p class="upload-container__title">View QHA data</p>
    <div class="search-params">
      <div class="dateFilter" style="float: left;">
        Upload from:
        <input type="date" name="search" v-model="dateRange.start"/>
        To:
        <input type="date" name="search" v-model="dateRange.end"/>
      </div>
      <div class="search-bar field no-margin" style="float: right; width: 30%;">
        <span class="icon input-icon">
          <i class="fas fa-search"></i>
        </span>
        <input
          type="text"
          class="input has-icon"
          placeholder="Search by health professional"
          v-model="searchTerm"
        />
      </div>
    </div>
    <div class="list__options">
      <label for="page-size"> Results per page: </label>&nbsp;
      <input
        class="input"
        type="number"
        step="5"
        name="page-size"
        v-model.number="limit"
      />
    </div>
    <div>
      <div class="table-wrapper">
        <table class="table">
          <thead>
          <tr>
            <th @click="sortBy('internalId')">*C/YP ID</th>
            <th @click="sortBy('stage')">Service</th>
            <th @click="sortBy('healthProfessional')">Health Professional</th>
            <th @click="sortBy('stage')">Key Stage</th>
            <th @click="sortBy('need')">Primary Need</th>
            <th @click="sortBy('settingName')">Setting Name</th>
            <th @click="sortBy('reportDate')">Report Date</th>
            <th @click="sortBy('historical')">Is Historical</th>
            <th></th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(plan, i) in paginatedQha" :key="i">
            <td>{{ plan.internalId }}</td>
            <td @dblclick="editField(plan, plan.service)">
              {{ plan.service }}
            </td>
            <td @dblclick="editField(plan, plan.healthProfessional)">
              {{ plan.healthProfessional }}
            </td>
            <td @dblclick="editField(plan, plan.stage)">
              {{ plan.stage }}
            </td>
            <td @dblclick="editField(plan, plan.need)">{{ plan.need }}</td>
            <td @dblclick="editField(plan, plan.settingName)">
              {{ plan.settingName }}
            </td>
            <td @dblclick="editField(plan, plan.reportDate)">
              {{ plan.reportDate | formatDate }}
            </td>
            <td>{{ plan.historical }}</td>
            <td class="archive" @click="archive(plan._id)">
              <i class="far fa-trash-alt"></i>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="list__controls">
      <vue-paginate-al
        :totalPage="totalPages"
        :currentPage="currentPage"
        @btnClick="goToFunction"
        customActiveBGColor="#00b3bb"
        :pageRange="4"
        nextText=">"
        prevText="<"
      >
      </vue-paginate-al>
    </div>
    <div class="button-row">
    </div>
    <div class="modal" :class="{ 'is-active': isEditing }">
      <div class="modal-background" @click.stop="isEditing = false"></div>
      <div class="modal-content">
        <div class="modal-title">Edit</div>
        <hr/>
        <div class="field">
          <label for="name">{{ formattedName }}</label>
          <input
            type="text"
            class="input edit-input"
            v-model="editingData"
            @change="canSave = true"
          />
        </div>
        <div class="unassigned">
        </div>
        <div class="modal-footer">
          <button
            class="button"
            :class="{ disabled: canSave !== true }"
            @click="saveEdit"
          >
            Save
          </button>
          <button class="button secondary" @click="isEditing = false">
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { format } from 'date-fns'
import { displayWarning } from '@/utils/notification-factory'
import { message } from '@/utils/app-messages'
import isBefore from 'date-fns/isBefore'
import isAfter from 'date-fns/isAfter'
import Fuse from 'fuse.js'

export default {
  name: 'ViewQHAData',
  data() {
    return {
      sortKey: 'updatedAt',
      editable: false,
      isEditing: false,
      editingProp: null,
      formattedName: null,
      editingData: null,
      editPlan: null,
      canSave: false,
      limit: 10,
      offset: 0,
      orderToggle: false,
      lastPage: 0,
      searchTerm: '',
      dateRange: {
        start: null,
        end: null
      }
    }
  },
  watch: {
    offset(newValue, oldValue) {
      this.offset = newValue
    },
    limit(newValue, oldValue) {
      this.limit = newValue
    }
  },
  computed: {
    ...mapGetters({ _plans: 'plan/plans' }),
    plans() {
      return [...this._plans].sort((a, b) =>
        a.updatedAt > b.updatedAt ? -1 : 1
      )
    },
    orderedAndFiltered() {
      const keys = this.sortKey.split('.')
      let assignments = [...this.plans]

      if (this.dateRange.start) {
        assignments = assignments.filter(({ createdAt }) =>
          isAfter(
            new Date(createdAt.slice(0, 10)),
            new Date(this.dateRange.start)
          )
        )
      }

      if (this.dateRange.end) {
        assignments = assignments.filter(({ createdAt }) =>
          isBefore(
            new Date(createdAt.slice(0, 10)),
            new Date(this.dateRange.end)
          )
        )
      }

      assignments.sort((a, b) => {
        for (let i = 0; i < keys.length; i++) {
          a = a[keys[i]]
          b = b[keys[i]]
        }
        return this.orderToggle ? (a < b ? -1 : 1) : a > b ? -1 : 1
      })

      const fuse = new Fuse(assignments, {
        includeScore: true,
        threshold: 0.5,
        keys: [
          'healthProfessional'
        ]
      })

      return this.searchTerm.length < 3
        ? assignments
        : fuse.search(this.searchTerm).map(({ item }) => item)
    },
    totalPages() {
      return Math.ceil(this.orderedAndFiltered.length / this.limit)
    },
    currentPage() {
      if (this.offset === 0) {
        return 1
      }
      return this.offset / this.limit
    },
    paginatedQha() {
      const limit = this.offset + this.limit
      return this.orderedAndFiltered.slice(this.offset, limit)
    }
  },
  filters: {
    formatDate(val) {
      return format(new Date(val), 'PPP')
    }
  },
  methods: {
    ...mapActions({ archivePlan: 'plan/archive' }),
    editField(plan, selected) {
      console.log('editField')
      try {
        const [property, data] = Object.entries(plan).filter(
          p => p[1] === selected
        )[0]
        const lookup = {
          finalised: 'Finalised Date',
          input: 'Current Social Care',
          internalId: 'C/YP ID',
          stage: 'Key Stage',
          need: 'Primary Need',
          settingName: 'Setting Name',
          settingType: 'Setting Type'
        }
        console.log({ property, data, formattedName: lookup[property] })
        this.editPlan = plan
        this.editingProp = property
        this.formattedName = lookup[property]
        this.editingData = plan[property]
        this.isEditing = true
      } catch (err) {
        displayWarning(message.FAILED_TO_EDIT_ITEM)
        console.log({ err })
        this.editPlan = null
        this.canSave = false
      }
    },
    async saveEdit(plan) {
      try {
        const { data } = await this.ApiService.updatePlan(this.editPlan._id, {
          [this.editingProp]: this.editingData
        })
        location.reload()
        console.log({ data })
      } catch (err) {
        displayWarning(message.FAILED_TO_SAVE_ITEM)
        console.log({ err })
      }
      this.isEditing = false
      this.canSave = false
      this.editPlan = null
      this.editingProp = null
      this.formattedName = null
      this.editingData = null
    },
    async archive(planId) {
      await this.archivePlan(planId)
    },
    goToFunction(value) {
      if (value === this.lastPage) return
      this.offset =
        value > this.lastPage
          ? this.offset + this.limit
          : this.offset - this.limit
      this.lastPage = value
    },
    sortBy(toSort) {
      this.offset = 0
      this.orderToggle = this.sortKey === toSort ? !this.orderToggle : true
      console.log({ orderToggle: this.orderToggle })
      this.sortKey = toSort
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/style/variables.scss';
@import '@/style/components.scss';
@import '@/style/sections.scss';
@import '@/style/modal.scss';

.upload-container {
  width: 100%;
  position: relative;
  display: grid;
  grid-template-rows: 46px repeat(3, auto);
  grid-auto-flow: unset;

  &__title {
    margin: 0;
    font-weight: 600;
    font-size: 1.25rem;
  }

  &__subtitle {
    font-size: 1rem;
    font-weight: 400;
    padding-left: 1rem;

    &--bold {
      font-weight: 600;
    }
  }

  &__dropbox {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100px;
    border: 1px dashed lightgrey;
    border-radius: $border-radius;
    padding: 1rem;
    margin: 1rem;
    font-size: 1.25rem;
  }

  .is-active {
    background: #42ab983b;
  }

  .button-row {
    padding-top: $padding1x;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
}

.archive:hover {
  transform: scale(1.3);
}

.modal-content {
  max-width: 600px;
  width: 100%;
}

.edit-input {
  margin-top: 0.5rem;
  padding-left: 0.5rem;
  border-radius: 4px;
}

.disabled {
  user-select: none;
  opacity: 0.5;
}

.control {
  display: flex;
  place-items: center;
  place-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid #eeedec;
  border-radius: 4px;
  background: transparent;
  box-shadow: none;
  transition: box-shadow 200ms;
  font-weight: 600;

  &:hover {
    box-shadow: 0 4px 8px #eeedec;
  }
}

.list__options {
  margin-top: 1rem;
  justify-self: flex-end;

  .input {
    max-width: 64px;
    padding-left: 1rem;
    font-weight: 600;
  }
}

.list__controls {
  justify-content: space-between;
}

.list__controls,
.pagination-control {
  display: flex;
}

.pagination-control .control {
  border: none;
}

.link-hover:hover {
  cursor: pointer;
  text-decoration: underline;
}

.current-page {
  color: blue;
  text-decoration: underline;
}

th {
  cursor: pointer;
}

.list__controls {
  grid-column: 1 / 2;
  justify-content: center;
  margin-bottom: 20px;
}

::v-deep .pagination li a {
  color: black !important;
}

::v-deep .pagination li:hover {
  a {
    text-decoration: underline;
  }
}

::v-deep .pagination .active a {
  color: white !important;
}
</style>
